import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useSetQuestionStatOverrideMutation } from '@/api/live-scoring/setQuestionStatOverride';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

interface QuickStatOverrideDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  questionId: string;
  fixtureId: string; // Keep for display context
  playerId?: string;
  teamId?: string;
  statType?: string;
  currentValue?: number;
  onSuccess?: () => void;
}

export function QuickStatOverrideDialog({
  open,
  onOpenChange,
  questionId,
  fixtureId,
  playerId,
  teamId,
  statType,
  currentValue,
  onSuccess,
}: QuickStatOverrideDialogProps) {
  const [value, setValue] = useState(currentValue?.toString() || '');
  const [reason, setReason] = useState('');

  const setOverrideMutation = useSetQuestionStatOverrideMutation();

  // Reset form when dialog opens/closes or props change
  useEffect(() => {
    if (open) {
      setValue(currentValue?.toString() || '');
      setReason('');
    }
  }, [open, currentValue]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent event from bubbling to parent forms

    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      toast.error('Please enter a valid number');
      return;
    }

    if (!statType) {
      toast.error('Stat type is required');
      return;
    }

    try {
      const result = await setOverrideMutation.mutateAsync({
        questionId,
        fixtureId,
        playerId,
        teamId,
        statType,
        value: numValue,
        originalValue: currentValue,
        reason: reason || undefined,
      });

      if (result?.scored) {
        toast.success('Override applied. Scores and payments have been recalculated.');
      } else if (result?.scoringSkippedReason) {
        // Override + re-grade succeeded, but the pickem could not be re-scored
        // (e.g. scoring already running, or payouts already paid). Warn so the
        // admin knows points/payments may need a manual "Update Scores".
        toast.warning(
          `Override applied, but scores/payments were not recalculated: ${result.scoringSkippedReason}`
        );
      } else {
        toast.success(
          'Override applied successfully. This question is now in manual scoring mode.'
        );
      }

      onOpenChange(false);
      onSuccess?.();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to set override');
    }
  };

  const entityLabel = playerId ? `Player ${playerId}` : teamId ? `Team ${teamId}` : 'Unknown';

  // While the mutation is in flight the backend is re-grading the question and
  // synchronously re-scoring the pickem (points + payout recalculation), which
  // can take a few seconds. Show a blocking, non-dismissable modal for the whole
  // duration; it disappears automatically when the request settles.
  const isProcessing = setOverrideMutation.isPending;

  return (
    <>
      <Dialog open={isProcessing} onOpenChange={() => {}} modal>
        <DialogContent
          className="sm:max-w-[420px] [&>button]:hidden"
          onPointerDownOutside={e => e.preventDefault()}
          onEscapeKeyDown={e => e.preventDefault()}
          onInteractOutside={e => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Loader2 className="h-5 w-5 animate-spin" />
              Recalculating scores &amp; payments
            </DialogTitle>
            <DialogDescription>
              The override has been applied and the pickem is being re-scored. Points and payment
              records are being recalculated — this may take a few seconds. Please don&apos;t close
              this window.
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Override Stat Value</DialogTitle>
            <DialogDescription>
              Set a manual override for this stat. The override will apply to THIS QUESTION ONLY.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              {/* Context info */}
              <div className="bg-gray-50 dark:bg-gray-900 border rounded p-3 text-sm space-y-1">
                <div>
                  <strong>Entity:</strong> {entityLabel}
                </div>
                <div>
                  <strong>Stat:</strong> {statType?.replace(/_/g, ' ') || 'N/A'}
                </div>
                <div>
                  <strong>Current Value:</strong>{' '}
                  {currentValue !== undefined ? currentValue : 'N/A'}
                </div>
              </div>

              {/* New value input */}
              <div className="space-y-2">
                <Label htmlFor="value">New Value *</Label>
                <Input
                  id="value"
                  type="number"
                  step="0.1"
                  value={value}
                  onChange={e => setValue(e.target.value)}
                  placeholder="Enter new stat value"
                  required
                  autoFocus
                />
              </div>

              {/* Reason input */}
              <div className="space-y-2">
                <Label htmlFor="reason">Reason (optional)</Label>
                <Textarea
                  id="reason"
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                  placeholder="e.g., Feed service showing incorrect value"
                  rows={2}
                />
              </div>

              {/* Info notice */}
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded p-3 text-sm text-blue-900 dark:text-blue-200">
                <strong>Note:</strong> This will override the stat for THIS QUESTION ONLY. The
                question will automatically enter manual scoring mode.
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={setOverrideMutation.isPending}>
                {setOverrideMutation.isPending ? 'Saving...' : 'Save Override'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
